import { type FC, useState } from 'react'
import { Button } from '../../../components/Button/Button'
import { LinkButton } from '../../../components/Button/LinkButton'
import { IntegrationIcon } from '../../../components/IntegrationIcon'
import { type IntegrationServiceId } from '../../../common/integrations'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import { useGetMeQuery } from '../../../store/user/user'
import { useGetUserSchedulingSettingsQuery, useUpdateUserSchedulingSettingsMutation } from '../../../store/scheduling/scheduling'
import { CalendarProvider, MeetingPlatformProvider } from '../../../models/Scheduling'
import { OnboardingStepHeader } from '../../Growth/Onboarding/OnboardingStepHeader/OnboardingStepHeader'
import { useIntegrations } from '../../Integration/useIntegrations'
import { ConnectedDetail, IntegrationOptionRow, ReconnectDetail } from './IntegrationOptionRow'
import { NATIVE_PLATFORM_BY_CALENDAR } from '../../ProfileSettings/Scheduling/sections/schedulingIntegrationStatus'
import { OnboardingStepContent } from './OnboardingStepFrame'
import { getIntegrationEmail, IntegrationConnectionStatus, resolveCalendarOptions } from './onboardingIntegrations'

const INTEGRATION_SETTINGS_PATH = '/account/integration-settings/integrations'
const connectPathFor = (serviceId: IntegrationServiceId) => `/account/integration-settings/integrations/${serviceId}/settings?connect`
const RECONNECT_TOOLTIP = "Your calendar is connected but Scheduling can't add meetings to it yet. Reconnect your calendar to fix this."

export interface CalendarStepProps {
  onCompleted?: () => void
}

export const CalendarStep: FC<CalendarStepProps> = ({ onCompleted }) => {
  const dispatch = useAppDispatch()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const { accountId, id: userId } = authenticatedUser
  const { data: me } = useGetMeQuery({ accountId })
  const { data: userSettings } = useGetUserSchedulingSettingsQuery({ accountId, userId })
  const { isIntegrationAllowed, isLoading: isIntegrationsLoading } = useIntegrations()
  const [updateUserSettings, { isLoading: isSelecting }] = useUpdateUserSchedulingSettingsMutation()
  const [selectedProvider, setSelectedProvider] = useState<CalendarProvider>()

  if (!userSettings || isIntegrationsLoading) {
    return null
  }

  const options = resolveCalendarOptions(userSettings.integrations, isIntegrationAllowed)
  const connected = options.filter((option) => option.status !== IntegrationConnectionStatus.NOT_CONNECTED)
  const connectable = options.filter((option) => option.status === IntegrationConnectionStatus.NOT_CONNECTED)

  const selectCalendar = async (provider: CalendarProvider) => {
    const currentPlatform = userSettings.integrations.selectedMeetingPlatform
    const isCurrentPlatformNative = currentPlatform === MeetingPlatformProvider.GOOGLE_MEET || currentPlatform === MeetingPlatformProvider.MS_TEAMS
    const clearsMeetingPlatform = isCurrentPlatformNative && currentPlatform !== NATIVE_PLATFORM_BY_CALENDAR[provider]

    const result = await updateUserSettings({
      accountId,
      userId,
      update: {
        integrations: {
          ...userSettings.integrations,
          selectedCalendar: provider,
          selectedMeetingPlatform: clearsMeetingPlatform ? null : currentPlatform,
        },
      },
    })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not select the calendar', messages: ['Please try again.'] }))
    } else {
      onCompleted?.()
    }
  }

  const manageLink =
    connectable.length > 0 ? (
      <LinkButton variant='primaryLinkButton' to={INTEGRATION_SETTINGS_PATH} className='text-decoration-none'>
        Manage your calendar integrations
      </LinkButton>
    ) : null

  if (connected.length === 0) {
    return (
      <>
        <OnboardingStepHeader
          icon={<i className='bi bi-calendar2' />}
          title='Connect your calendar'
          subtitle='Scheduling books meetings straight into your calendar. Connect one to get started.'
        />
        <OnboardingStepContent>
          <div className='d-flex gap-3 justify-content-center'>
            {connectable.map((option) => (
              <div key={option.serviceId} className='d-flex flex-column align-items-center gap-2 p-4 border rounded-3 zocks-border-color-gray-300'>
                <IntegrationIcon serviceId={option.serviceId} width='1.5rem' height='1.5rem' />
                <span className='paragraph-small fw-bold zocks-text-gray-900'>{option.label}</span>
                <LinkButton to={connectPathFor(option.serviceId)} variant='primaryLinkButton' className='text-decoration-none'>
                  Connect
                </LinkButton>
              </div>
            ))}
          </div>
        </OnboardingStepContent>
      </>
    )
  }

  if (connected.length === 1) {
    const option = connected[0]
    const email = getIntegrationEmail(me?.integrations, option.serviceId)
    const isReady = option.status === IntegrationConnectionStatus.READY

    return (
      <>
        <OnboardingStepHeader
          icon={<i className='bi bi-calendar2' />}
          title={isReady ? 'Confirm your scheduling calendar' : 'Allow Scheduling to add meetings'}
          subtitle={
            isReady
              ? 'Scheduling adds booked meetings to this calendar.'
              : "Your calendar is connected but Scheduling can't add meetings to it yet. Reconnect your calendar to fix this."
          }
        />
        <OnboardingStepContent>
          <div className='d-flex flex-column gap-3 p-3 border rounded-3 zocks-border-color-gray-300 w-100'>
            <div className='d-flex align-items-center gap-2'>
              <IntegrationIcon serviceId={option.serviceId} width='1.5rem' height='1.5rem' />
              <span className='d-flex flex-column overflow-hidden'>
                <span className='paragraph-small fw-bold zocks-text-gray-900'>{option.label}</span>
                {isReady ? <ConnectedDetail email={email} /> : <span className='paragraph-small zocks-text-gray-600 text-truncate'>{email}</span>}
              </span>
            </div>
            {isReady ? (
              <Button variant='primary' className='align-self-stretch' loading={isSelecting} onClick={() => void selectCalendar(option.provider)}>
                Select calendar
              </Button>
            ) : (
              <LinkButton to={connectPathFor(option.serviceId)} variant='primary' className='align-self-stretch'>
                Reconnect
              </LinkButton>
            )}
          </div>
          {manageLink}
        </OnboardingStepContent>
      </>
    )
  }

  const selectedOption =
    connected.find((option) => option.provider === selectedProvider) ??
    connected.find((option) => option.provider === userSettings.integrations.selectedCalendar) ??
    connected[0]
  const isSelectedReady = selectedOption.status === IntegrationConnectionStatus.READY

  return (
    <>
      <OnboardingStepHeader
        icon={<i className='bi bi-calendar2' />}
        title='Choose your scheduling calendar'
        subtitle='Scheduling adds booked meetings to one calendar. Choose which one to use.'
      />
      <OnboardingStepContent>
        <div role='radiogroup' aria-label='Scheduling calendar' className='d-flex flex-column gap-2 w-100'>
          {connected.map((option) => (
            <IntegrationOptionRow
              key={option.serviceId}
              serviceId={option.serviceId}
              label={option.label}
              detail={
                option.status === IntegrationConnectionStatus.READY ? (
                  <ConnectedDetail email={getIntegrationEmail(me?.integrations, option.serviceId)} />
                ) : (
                  <ReconnectDetail email={getIntegrationEmail(me?.integrations, option.serviceId)} tooltip={RECONNECT_TOOLTIP} />
                )
              }
              selected={option.provider === selectedOption.provider}
              onSelect={() => setSelectedProvider(option.provider)}
            />
          ))}
        </div>
        {isSelectedReady ? (
          <Button variant='primary' className='align-self-stretch' loading={isSelecting} onClick={() => void selectCalendar(selectedOption.provider)}>
            Select calendar
          </Button>
        ) : (
          <LinkButton to={connectPathFor(selectedOption.serviceId)} variant='primary' className='align-self-stretch'>
            Reconnect
          </LinkButton>
        )}
      </OnboardingStepContent>
    </>
  )
}
